import { updateStoreMembers } from '~/utils'
import { Userpilot } from 'userpilot'
import Store from '~/store'
import get from 'lodash/get'
import AuthAPI from 'api/AuthAPI'
import UsersAPI from 'api/UsersAPI'
import PracticesAPI from 'api/PracticesAPI'
import FacilityServicesAPI from 'api/FacilityServicesAPI'
import SignupStepService from './SignupStepService'
import UserRolesEnum from '../enums/UserRolesEnum'
import SkipStepService from './SkipStepService'
import MessagesAPI from 'api/MessagesAPI'
import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
import PracticePlanEnum from '@/enums/PracticePlanEnum'

export default {
    logIn(accessToken, idToken, refreshToken) {
        localStorage.removeItem('activeMember')
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('idToken', idToken)
        localStorage.setItem('refreshToken', refreshToken)
        localStorage.setItem('referralsCompleted', false)
    },

    setActiveMember({ id }) {
        localStorage.setItem('activeMember', id)
    },

    setActiveUserLocations(id) {
        PracticesAPI.getUserLocations(id).then((locations) => Store.dispatch('auth/setUserLocations', locations))
    },

    setLastRegisteredPhysician(physican) {
        Store.dispatch('auth/setLastRegisteredPhysician', physican)
    },

    getToken() {
        return localStorage.getItem('idToken')
    },

    loadSession(force) {
        if (!this.isLoggedIn()) {
            return Promise.resolve()
        }

        if (get(this.getUser(), 'id') && !force) {
            return Promise.resolve()
        }

        return Promise.resolve(UsersAPI.getCurrent().then((user) => Store.dispatch('auth/setUser', user))).then(() => {
            if (this.isUserPracticeDemo()) {
                Userpilot.identify(get(this.getUser(), 'id'), {
                    name: get(this.getUser(), 'firstName'),
                    email: get(this.getUser(), 'email'),
                })
            }
            if (!this.getUser().practiceId) return false
            return Promise.resolve(PracticesAPI.getMy().then((practice) => Store.dispatch('auth/setPractice', practice))).then(
                () => {
                    if (!this.getPractice() || !this.getPractice().id) {
                        return {}
                    }
                    const userId = this.isUserPhysician()
                        ? get(this.getUser(), 'id')
                        : localStorage.getItem('activeMember') || get(this.getUser(), 'id')
                    const practiceId = get(this.getPractice(), 'id')
                    if (userId) {
                        PracticesAPI.getUserLocations(userId).then((locations) =>
                            Store.dispatch('auth/setUserLocations', locations),
                        )
                        MessagesAPI.getUnread({ toUserId: userId, toPracticeId: practiceId }, ['id']).then((messages) =>
                            Store.dispatch('chat-messages/setUnread', messages),
                        )
                    }

                    if (this.getPractice().practiceType === PracticeTypeEnum.ANCILLARY) {
                        this.updateFacilityServicesByLocation()
                    }
                    return updateStoreMembers()
                },
            )
        })
    },

    logOut() {
        const token = this.getToken()
        localStorage.removeItem('accessToken')
        localStorage.removeItem('idToken')
        localStorage.removeItem('refreshToken')
        Store.dispatch('auth/clear')
        SignupStepService.resetAllShowSignupSteps()
        SkipStepService.resetAllShowSignupSteps()

        if (token) {
            return AuthAPI.logOut()
        }
    },

    isLoggedIn() {
        return Boolean(this.getToken())
    },

    isUserPhysician() {
        return [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(get(this.getUser(), 'userRole.id'))
    },

    isActiveMemberPhysician() {
        return [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(
            get(this.getActiveMember(), 'userRole.id'),
        )
    },

    isUserManager() {
        return get(this.getUser(), 'userRole.name') === UserRolesEnum.OFFICE_MANAGER.NAME
    },

    isUserAdministrator() {
        return get(this.getUser(), 'userRole.name') === UserRolesEnum.ADMINISTRATOR.NAME
    },

    isMasterUser() {
        return this.getUser().isMasterUser
    },

    isPracticeAncillary() {
        return this.getPractice().practiceType === PracticeTypeEnum.ANCILLARY
    },

    isPracticePremium() {
        return this.getPractice().subscription === PracticePlanEnum.PREMIUM
    },
    isPracticeOther() {
        return this.getPractice().practiceType === PracticeTypeEnum.OTHER
    },

    isUserOther() {
        return [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID, UserRolesEnum.OFFICE_MANAGER.ID].includes(
            get(this.getUser(), 'userRole.id'),
        )
    },

    isUserPracticeOther() {
        return (
            get(this.getUser(), 'practiceType') === PracticeTypeEnum.OTHER ||
            (this.getPractice() && this.getPractice().practiceType === PracticeTypeEnum.OTHER)
        )
    },

    isUserPracticeDemo() {
        const desiredValues = ['demo-physician', 'demo-ancillary', 'demo-primary-care']
        const filteredValues = Object.values(PracticeTypeEnum).filter((value) => desiredValues.includes(value))
        return filteredValues.includes(this.getUser()?.practiceType)
    },

    wasUserDemo() {
        return get(this.getUser(), 'wasDemo')
    },

    getActiveMember() {
        if (this.isUserPracticeOther()) {
            return this.getUser()
        }
        const memberId = localStorage.getItem('activeMember') || get(this.getUser(), 'id')
        return this.getMembers().find(({ id }) => parseInt(id) === parseInt(memberId))
    },

    getPracticeMember(memberId) {
        return this.getMembers().find(({ id }) => parseInt(id) === parseInt(memberId))
    },

    getUser() {
        return Store.getters['auth/getUser'] || {}
    },

    getPractice() {
        return Store.getters['auth/getPractice']
    },

    updatePractice(practice) {
        Store.dispatch('auth/setPractice', practice)
    },

    forceUpdatePractice() {
        return PracticesAPI.getMy().then((practice) => Store.dispatch('auth/setPractice', practice))
    },

    updateFacilityServicesByLocation() {
        return FacilityServicesAPI.getAncillaryServicesByLocation(this.getPractice().id).then((services) =>
            Store.dispatch('auth/setFacilityServicesByLocation', services),
        )
    },

    getMembers() {
        return Store.getters['auth/getMembers']
    },

    getLastRegisteredPhysician() {
        return Store.getters['auth/getLastRegisteredPhysician']
    },

    getUserLocations() {
        return Store.getters['auth/getUserLocations']
    },

    getFacilityServicesByLocation() {
        return Store.getters['auth/getFacilityServicesByLocation']
    },

    refreshPracticeMembers() {
        return updateStoreMembers()
    },

    hasUserHaveFindProviderSearch() {
        return (
            this.getUser().id === Number(localStorage.getItem('findProviderUserId')) && localStorage.getItem('findProviderQuery')
        )
    },
}
